import { TransactionSyncStatus } from '../../graphql/generated';
import { dt } from '../../util/dateTime';
import { typedEntries } from '../../util/objects';
import { isNotNullish } from '../../util/types';
import { useFinancialEntitiesContext } from '../financialEntities';

type GlobalTransactionSyncStatus =
    | 'waiting-for-webhook'
    | 'fetching'
    | 'synced'
    | 'failed'
    | 'categorizing';

const globalStatusMappings: Record<
    Exclude<GlobalTransactionSyncStatus, 'loading'>,
    TransactionSyncStatus[]
> = {
    // These are in priority order
    'waiting-for-webhook': ['INITIAL_WAITING_FOR_WEBHOOK'],
    fetching: [
        'UPDATE_FETCHING_FROM_PROVIDER',
        'INITIAL_FETCHING_FROM_PROVIDER',
    ],
    categorizing: ['UPDATE_CATEGORIZING', 'INITIAL_CATEGORIZING'],
    synced: ['INITIAL_SYNCED', 'UPDATE_SYNCED'],
    failed: ['INITIAL_SYNC_FAILED', 'UPDATE_SYNC_FAILED'],
};

export const useGlobalTransactionSyncStatus = ():
    | GlobalTransactionSyncStatus
    | undefined => {
    const { fes } = useFinancialEntitiesContext();

    if (!fes.unfilteredData) return;

    const now = dt();

    const allStatuses = fes.unfilteredData.financialEntities
        .filter(
            (fe) =>
                fe.type === 'CheckingAccount' ||
                fe.type === 'CreditCard' ||
                fe.type === 'SavingsAccount'
        )
        .filter((fe) => {
            // Only include FEs that have been waiting for webhook for less than 2 minutes
            if (fe.transactionSyncStatus === 'INITIAL_WAITING_FOR_WEBHOOK') {
                const minutesSinceCreation = now.diff(
                    dt(fe.createdAt),
                    'minutes'
                );
                return minutesSinceCreation <= 2;
            }

            return true;
        })
        .map((fe) => fe.transactionSyncStatus)
        .filter(isNotNullish);

    for (const [globalStatus, patterns] of typedEntries(globalStatusMappings)) {
        if (allStatuses.some((status) => patterns.includes(status))) {
            return globalStatus;
        }
    }
};
